import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import {
  Button,
  Input,
  Label,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  useToast,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@lead-magnifier/ui'
import {
  UserPlus,
  Users,
  MoreVertical,
  Mail,
  Shield,
  User,
  Trash2,
  Clock,
  Calendar,
  FileText,
  Copy,
  Upload,
} from 'lucide-react'
import {
  useTeam,
  useInviteUser,
  useRevokeInvitation,
  useUpdateMemberRole,
  useRemoveMember,
} from '@/hooks/use-team'
import { useAuth } from '@/lib/auth-context'
import { useWorkspace } from '@/lib/workspace-context'
import { BulkInviteDialog } from '@/components/bulk-invite-dialog'

export const Route = createFileRoute('/_authenticated/team/')({
  component: TeamPage,
})

function TeamPage() {
  const { user } = useAuth()
  const { activeWorkspace, isAdmin } = useWorkspace()
  const { data, isLoading, error } = useTeam()
  const inviteUser = useInviteUser()
  const revokeInvitation = useRevokeInvitation()
  const updateRole = useUpdateMemberRole()
  const removeMember = useRemoveMember()
  const { toast } = useToast()

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [bulkInviteOpen, setBulkInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'admin' | 'user'>('user')
  const [memberToRemove, setMemberToRemove] = useState<string | null>(null)

  const [inviteLink, setInviteLink] = useState<string | null>(null)

  const handleInvite = async () => {
    if (!inviteEmail) return

    try {
      const result = await inviteUser.mutateAsync({
        email: inviteEmail,
        role: inviteRole,
      })

      const inviteUrl = `${window.location.origin}/invite/${result.token}`
      setInviteLink(inviteUrl)

      toast({
        title: 'Einladung erstellt',
        description: `Einladungslink wurde generiert.`,
      })

      setInviteEmail('')
      setInviteRole('user')
    } catch (err) {
      toast({
        title: 'Fehler',
        description: err instanceof Error ? err.message : 'Einladung fehlgeschlagen',
        variant: 'destructive',
      })
    }
  }

  const copyInviteLink = async () => {
    if (!inviteLink) return
    await navigator.clipboard.writeText(inviteLink)
    toast({ title: 'Link kopiert!' })
  }

  const handleRevokeInvitation = async (invitationId: string) => {
    try {
      await revokeInvitation.mutateAsync(invitationId)
      toast({ title: 'Einladung zurückgezogen' })
    } catch {
      toast({ title: 'Fehler', variant: 'destructive' })
    }
  }

  const handleUpdateRole = async (memberId: string, role: 'admin' | 'user') => {
    try {
      await updateRole.mutateAsync({ memberId, role })
      toast({ title: 'Rolle aktualisiert' })
    } catch {
      toast({ title: 'Fehler', variant: 'destructive' })
    }
  }

  const handleRemoveMember = async () => {
    if (!memberToRemove) return
    try {
      await removeMember.mutateAsync(memberToRemove)
      toast({ title: 'Mitglied entfernt' })
      setMemberToRemove(null)
    } catch {
      toast({ title: 'Fehler', variant: 'destructive' })
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('de-AT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const roleLabels = {
    owner: { label: 'Inhaber', variant: 'default' as const, icon: Shield },
    admin: { label: 'Admin', variant: 'secondary' as const, icon: Shield },
    user: { label: 'Aussteller', variant: 'outline' as const, icon: User },
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-destructive">Fehler beim Laden des Teams.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Team</h1>
          <p className="text-muted-foreground mt-1">
            Verwalte dein Team in {activeWorkspace?.name}
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setBulkInviteOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />
              CSV-Import
            </Button>
          <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="h-4 w-4 mr-2" />
                Einladen
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {inviteLink ? 'Einladung erstellt' : 'Neuen Aussteller einladen'}
                </DialogTitle>
                <DialogDescription>
                  {inviteLink
                    ? 'Teile diesen Link mit dem neuen Aussteller.'
                    : 'Erstelle eine Einladung für einen neuen Aussteller oder Admin.'}
                </DialogDescription>
              </DialogHeader>

              {inviteLink ? (
                <div className="space-y-4 py-4">
                  <div className="flex gap-2">
                    <Input value={inviteLink} readOnly className="font-mono text-sm" />
                    <Button onClick={copyInviteLink} variant="outline">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Der Link ist 7 Tage gültig. Du kannst ihn per E-Mail oder Messenger teilen.
                  </p>
                  <DialogFooter>
                    <Button onClick={() => {
                      setInviteLink(null)
                      setInviteDialogOpen(false)
                    }}>
                      Fertig
                    </Button>
                  </DialogFooter>
                </div>
              ) : (
                <>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">E-Mail-Adresse</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="name@beispiel.at"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="role">Rolle</Label>
                      <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as 'admin' | 'user')}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4" />
                              Aussteller
                            </div>
                          </SelectItem>
                          <SelectItem value="admin">
                            <div className="flex items-center gap-2">
                              <Shield className="h-4 w-4" />
                              Admin
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        {inviteRole === 'admin'
                          ? 'Admins können alle Events und Leads sehen und andere Aussteller einladen.'
                          : 'Aussteller können eigene Events erstellen und nur ihre eigenen Leads sehen.'}
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
                      Abbrechen
                    </Button>
                    <Button onClick={handleInvite} disabled={!inviteEmail || inviteUser.isPending}>
                      {inviteUser.isPending ? 'Wird erstellt...' : 'Einladung erstellen'}
                    </Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>
          <BulkInviteDialog open={bulkInviteOpen} onOpenChange={setBulkInviteOpen} />
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Aussteller gesamt
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.members.length || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Offene Einladungen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.invitations.length || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Deine Rolle
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant={roleLabels[activeWorkspace?.role as keyof typeof roleLabels]?.variant || 'outline'}>
              {roleLabels[activeWorkspace?.role as keyof typeof roleLabels]?.label || activeWorkspace?.role}
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* Team Members */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Aussteller
          </CardTitle>
          <CardDescription>Alle Aussteller und Admins in {activeWorkspace?.name}</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>E-Mail</TableHead>
                <TableHead>Rolle</TableHead>
                <TableHead>Events</TableHead>
                <TableHead>Leads</TableHead>
                <TableHead>Letzter Login</TableHead>
                {isAdmin && <TableHead className="w-[50px]"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.members.map((member) => {
                const roleInfo = roleLabels[member.role]
                const isCurrentUser = member.userId === user?.id
                const canModify = isAdmin && !isCurrentUser && member.role !== 'owner'

                return (
                  <TableRow key={member.id}>
                    <TableCell className="font-medium">
                      {member.name || '-'}
                      {isCurrentUser && (
                        <Badge variant="outline" className="ml-2">Du</Badge>
                      )}
                    </TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>
                      <Badge variant={roleInfo.variant}>
                        <roleInfo.icon className="h-3 w-3 mr-1" />
                        {roleInfo.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-muted-foreground" />
                        {member.eventsCount}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <FileText className="h-3 w-3 text-muted-foreground" />
                        {member.leadsCount}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {member.lastLoginAt ? formatDate(member.lastLoginAt) : 'Nie'}
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        {canModify && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleUpdateRole(member.id, member.role === 'admin' ? 'user' : 'admin')}
                              >
                                <Shield className="h-4 w-4 mr-2" />
                                {member.role === 'admin' ? 'Zu Aussteller' : 'Zu Admin'}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setMemberToRemove(member.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Entfernen
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {data?.invitations && data.invitations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Offene Einladungen
            </CardTitle>
            <CardDescription>Einladungen, die noch nicht angenommen wurden</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Rolle</TableHead>
                  <TableHead>Läuft ab</TableHead>
                  <TableHead>Gesendet</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.invitations.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium">{inv.email}</TableCell>
                    <TableCell>
                      <Badge variant={inv.role === 'admin' ? 'secondary' : 'outline'}>
                        {inv.role === 'admin' ? 'Admin' : 'Aussteller'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(inv.expiresAt)}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(inv.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRevokeInvitation(inv.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Remove Member Dialog */}
      <AlertDialog open={!!memberToRemove} onOpenChange={() => setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mitglied entfernen?</AlertDialogTitle>
            <AlertDialogDescription>
              Das Mitglied verliert den Zugang zu diesem Workspace. Seine Events und Leads bleiben erhalten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveMember} className="bg-destructive text-destructive-foreground">
              Entfernen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
